import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Form } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Select from 'react-select';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import Paginator from '~/Components/Paginator';
import AniversariantesService from '~/Services/GES/AniversariantesService';
import Filtros from './components/Filtros';
import GridAniversariantes from './components/GridAniversariantes';
import CardListMobile from './components/CardListMobile';

const TAMANHOS_PAGINA = [25, 50, 100];
const OPCOES_TAMANHO_PAGINA = TAMANHOS_PAGINA.map(t => ({
  value: t,
  label: String(t),
}));

// Yup (TechSpec §3.4): mês 1-12, dia 1-31, GE obrigatório.
const schema = Yup.object().shape({
  codigoGrupoEmpresa: Yup.number()
    .typeError('Selecione o grupo empresa.')
    .required('Selecione o grupo empresa.')
    .positive('Grupo empresa inválido.'),
  diaIni: Yup.number()
    .typeError('Dia inicial inválido.')
    .required('Informe o dia inicial.')
    .min(1, 'Dia mínimo é 1.')
    .max(31, 'Dia máximo é 31.'),
  mesIni: Yup.number()
    .typeError('Mês inicial inválido.')
    .required('Informe o mês inicial.')
    .min(1, 'Mês mínimo é 1.')
    .max(12, 'Mês máximo é 12.'),
  diaFim: Yup.number()
    .typeError('Dia final inválido.')
    .required('Informe o dia final.')
    .min(1, 'Dia mínimo é 1.')
    .max(31, 'Dia máximo é 31.'),
  mesFim: Yup.number()
    .typeError('Mês final inválido.')
    .required('Informe o mês final.')
    .min(1, 'Mês mínimo é 1.')
    .max(12, 'Mês máximo é 12.'),
  codigoVendedor: Yup.number()
    .nullable()
    .integer('Vendedor inválido.'),
});

const Aniversariantes = () => {
  const [opcoesGrupoEmpresa, setOpcoesGrupoEmpresa] = useState([]);
  const [opcoesVendedor, setOpcoesVendedor] = useState([]);
  const [carregandoVendedores, setCarregandoVendedores] = useState(false);
  const [itens, setItens] = useState([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(25);
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState('');
  const [jaBuscou, setJaBuscou] = useState(false);
  const [sessaoBusca, setSessaoBusca] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const formik = useFormik({
    initialValues: {
      codigoGrupoEmpresa: null,
      diaIni: 1,
      mesIni: 1,
      diaFim: 31,
      mesFim: 12,
      codigoVendedor: null,
    },
    validationSchema: schema,
    onSubmit: () => {},
  });

  const { values } = formik;

  // RNF-04: alterna entre grid (desktop) e CardListMobile (<= 576px).
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 576px)');
    const handler = e => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // GEs do usuário logado (RN-08).
  useEffect(() => {
    let ativo = true;
    AniversariantesService.listarGruposEmpresa()
      .then(res => {
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : [];
        setOpcoesGrupoEmpresa(
          lista.map(ge => ({
            value: ge.codigo_grupo_empresa,
            label: ge.apelido,
          }))
        );
      })
      .catch(err => {
        if (!ativo) return;
        const msg =
          err && err.message ? err.message : 'Erro ao carregar grupos empresa.';
        toast.error(msg);
      });
    return () => {
      ativo = false;
    };
  }, []);

  // Vendedores do GE (cascata com proteção anti-race — Ciclo 9 L3).
  useEffect(() => {
    const codigoGrupoEmpresa = values.codigoGrupoEmpresa;
    if (!codigoGrupoEmpresa) {
      setOpcoesVendedor([]);
      return undefined;
    }
    let ativo = true;
    setCarregandoVendedores(true);
    AniversariantesService.listarVendedores(codigoGrupoEmpresa)
      .then(res => {
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : [];
        setOpcoesVendedor(
          lista.map(v => ({
            value: v.codigo_conta,
            label: v.nome_prestador,
          }))
        );
      })
      .catch(err => {
        if (!ativo) return;
        setOpcoesVendedor([]);
        const msg =
          err && err.message ? err.message : 'Erro ao carregar vendedores.';
        toast.error(msg);
      })
      .finally(() => {
        if (ativo) setCarregandoVendedores(false);
      });
    return () => {
      ativo = false;
    };
  }, [values.codigoGrupoEmpresa]);

  // Vendedor selecionado fica órfão ao trocar de GE — limpa.
  useEffect(() => {
    formik.setFieldValue('codigoVendedor', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.codigoGrupoEmpresa]);

  const consultar = useCallback(
    async pagina => {
      const errosValidacao = await formik.validateForm();
      if (Object.keys(errosValidacao).length > 0) {
        const tocados = {};
        Object.keys(errosValidacao).forEach(c => {
          tocados[c] = true;
        });
        formik.setTouched(tocados);
        return;
      }

      setBuscando(true);
      setErro('');
      try {
        const params = {
          codigo_grupo_empresa: Number(values.codigoGrupoEmpresa),
          dia_ini: Number(values.diaIni),
          mes_ini: Number(values.mesIni),
          dia_fim: Number(values.diaFim),
          mes_fim: Number(values.mesFim),
          pagina,
          tamanho_pagina: tamanhoPagina,
        };
        if (values.codigoVendedor != null) {
          params.codigo_vendedor = Number(values.codigoVendedor);
        }

        const resposta = await AniversariantesService.listar(params);
        const lista =
          resposta && Array.isArray(resposta.itens) ? resposta.itens : [];
        const total =
          resposta && resposta.total_registros != null
            ? resposta.total_registros
            : lista.length;

        setItens(lista);
        setTotalRegistros(total);
        setPaginaAtual(pagina);
        setJaBuscou(true);
        if (lista.length === 0) setErro('Nenhum registro encontrado.');
      } catch (err) {
        setItens([]);
        setTotalRegistros(0);
        const msg =
          err && err.message ? err.message : 'Erro ao consultar aniversariantes.';
        setErro(msg);
        toast.error(msg);
      } finally {
        setBuscando(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values, tamanhoPagina]
  );

  const handleBuscar = () => {
    setSessaoBusca(s => s + 1);
    consultar(1);
  };

  const handleMudarPagina = pagina => {
    if (pagina !== paginaAtual) consultar(pagina);
  };

  const handleTamanhoPaginaChange = opt => {
    const novoTamanho = opt ? opt.value : 25;
    setTamanhoPagina(novoTamanho);
    setPaginaAtual(1);
    setSessaoBusca(s => s + 1);
  };

  const handleExportar = () => {
    if (itens.length === 0) {
      toast.warning('Nenhum dado para exportar.');
      return;
    }
    try {
      AniversariantesService.exportarExcel(itens);
    } catch (err) {
      const msg =
        err && err.message ? err.message : 'Erro ao exportar para Excel.';
      toast.error(msg);
    }
  };

  // Aviso de virada de ano (mesIni > mesFim) — UX correction (ADR-002).
  const viradaDeAno = Number(values.mesIni) > Number(values.mesFim);

  const totalPaginas = Math.max(1, Math.ceil(totalRegistros / tamanhoPagina));
  const indiceInicial =
    totalRegistros === 0 ? 0 : (paginaAtual - 1) * tamanhoPagina + 1;
  const indiceFinal = Math.min(paginaAtual * tamanhoPagina, totalRegistros);

  return (
    <Container fluid className="py-3">
      <Header title="Consulta de Aniversariantes" />

      <Card className="mt-3">
        <Card.Body>
          <Filtros
            formik={formik}
            opcoesGrupoEmpresa={opcoesGrupoEmpresa}
            opcoesVendedor={opcoesVendedor}
            carregandoVendedores={carregandoVendedores}
            onBuscar={handleBuscar}
            onExportar={handleExportar}
            podeExportar={itens.length > 0}
            buscando={buscando}
          />
          {viradaDeAno && (
            <div className="alert alert-info mt-3 mb-0">
              Período com virada de ano detectado (mês inicial maior que o final):
              serão considerados os aniversariantes do mês inicial até dezembro e
              de janeiro até o mês final.
            </div>
          )}
        </Card.Body>
      </Card>

      {buscando && <Loading label="Consultando aniversariantes..." />}

      {!buscando && erro && (
        <div className="alert alert-warning mt-3">{erro}</div>
      )}

      {!buscando && jaBuscou && itens.length > 0 && (
        <div className="mt-3">
          {isMobile ? (
            <CardListMobile dados={itens} />
          ) : (
            <GridAniversariantes dados={itens} />
          )}

          <Row className="align-items-center mt-3">
            <Col xs={12} md={4} className="mb-2 mb-md-0">
              <Form.Group className="mb-0 d-flex align-items-center">
                <Form.Label className="mb-0 mr-2">Itens por página:</Form.Label>
                <Select
                  options={OPCOES_TAMANHO_PAGINA}
                  value={
                    OPCOES_TAMANHO_PAGINA.find(o => o.value === tamanhoPagina) ||
                    null
                  }
                  onChange={handleTamanhoPaginaChange}
                  isClearable={false}
                  styles={{ container: base => ({ ...base, width: 100 }) }}
                />
              </Form.Group>
            </Col>
            <Col
              xs={12}
              md={4}
              className="d-flex justify-content-center mb-2 mb-md-0"
            >
              {totalPaginas > 1 && (
                <Paginator
                  key={sessaoBusca}
                  numberOfPages={totalPaginas}
                  initialPage={paginaAtual}
                  onPageChange={handleMudarPagina}
                />
              )}
            </Col>
            <Col xs={12} md={4} className="text-md-right">
              <small className="text-muted">
                Exibindo {indiceInicial}-{indiceFinal} de {totalRegistros}
              </small>
            </Col>
          </Row>
        </div>
      )}
    </Container>
  );
};

export default Aniversariantes;
